import { useState, useEffect, useRef, useCallback, createContext, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RouteObject } from "react-router-dom"

import { RenderOptions } from '@/constants'

import SpaceParser from '@/utils/spaceParser'
import Assistant from '@/utils/assistant/assistant'
import { RootState } from '@/utils/store/store'
import { useAuthentication } from '@/hooks/useAuthentication'

import { SpaceContextType, ParsedResources, Space } from '@/shared/types/space'
import { ChatMessage } from '@/shared/types/chat'
import { ApiParameters } from '@/shared/types/api'

const SpaceContext = createContext<SpaceContextType>({
    spaces: [],
    spacesLoading: true,
    spaceRoutes: [],
    switchingSpace: false,
    switchSpace: async () => { },
    switchAssistant: async () => { },
    loadAssistantMetadata: async () => { },
    getActiveSpace: () => undefined,
    getActiveAssistant: () => undefined,
    loadSpaceHistory: () => Promise.resolve([]),
    getAssistantByPath: () => undefined,
    spacesError: false,
    reloadSpaces: async () => { },
    activationError: false,
    retryActivation: async () => { },
    isActivating: false,
})

const SpaceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [spaces, setSpaces] = useState<Space[]>([])
    const [spacesLoading, setSpacesLoading] = useState(true)
    const [spaceRoutes, setSpaceRoutes] = useState<RouteObject[]>([])
    const [spacesError, setSpacesError] = useState(false)
    const [activationError, setActivationError] = useState(false)
    // The space and assistant flags (`isActive`) are set at the start of a switch, long before the history and the
    // metadata are there: this says when the switch is really over
    const [isActivating, setIsActivating] = useState(false)
    const activatingRef = useRef(0)
    const beginActivation = () => {
        activatingRef.current += 1
        setIsActivating(true)
    }
    const endActivation = () => {
        activatingRef.current = Math.max(0, activatingRef.current - 1)
        if (activatingRef.current === 0) setIsActivating(false)
    }
    const [switchingSpace, setSwitchingSpace] = useState(false)
    const [switchingSpaceId, setSwitchingSpaceId] = useState<string>()
    const { config } = useSelector((state: RootState) => state.application)
    const { information } = useSelector((state: RootState) => state.authentication)

    const { t } = useTranslation()
    const { rehydrateInformation, getApiToken } = useAuthentication()
    const switchingRef = useRef(false)
    const parseConfigIdRef = useRef<string | null>(null)

    // wait for config and authentication - filter based on user permissions
    useEffect(() => {
        if (!config || !information) return
        if (!information.userId) return  // wait for complete user information
        const configId = config.name + (config.defaultSpace ?? '') + (information.roles?.join(',') ?? '')
        if (parseConfigIdRef.current === configId) return
        parseConfigIdRef.current = configId

        parseRessources()
        // parseRessources is created again on every render: the effect runs when the config or the user changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config, information])

    useEffect(() => {
        const activeSpace = spaces.find(space => space.isActive)
        if (activeSpace || spaces.length === 0) return
        if (switchingRef.current) return

        const [urlSpaceId, urlAssistantPath] = window.location.pathname.split('/').filter(Boolean)
        const urlAssistant = getAssistantByPath(urlSpaceId, urlAssistantPath)

        const targetId = spaces.find(s => s.id === urlSpaceId)?.id
            || spaces.find(s => s.id === config?.defaultSpace)?.id
            || spaces[0]?.id

        const resolvedAssistantId = urlAssistant?.config.id

        if (targetId) switchSpace(targetId, resolvedAssistantId)
        // the functions are created again on every render: the effect runs when the spaces or the routes change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spaces, spaceRoutes])

    const getActiveSpace = () => {
        return spaces?.find(space => space.isActive)
    }

    const getActiveAssistant = () => {
        const activeSpace = getActiveSpace()

        return activeSpace?.assistants?.find(assistant => assistant.isActive)
    }

    const getAssistantByPath = (spaceId: string, assistantPath: string) => {
        const space = spaces.find(space => space.id === spaceId)
        const assistant = space?.assistants?.find(assistant => assistant.path === assistantPath)

        return assistant
    }

    const parseRessources = async () => {
        try {
            if (!config) throw new Error('the config is not loaded')

            const parsedThings = await SpaceParser.parse(config, RenderOptions.spaces, t) as ParsedResources

            const hasDefaultSpace = parsedThings.resources.find(resource => resource.id === config?.defaultSpace)

            if (!hasDefaultSpace) {
                const firstSpace = parsedThings.resources[0]
                if (firstSpace) {
                    console.warn('No default space found, setting first space as default:', firstSpace.id)
                }
            }

            setSpacesError(false)
            setSpaces(parsedThings.resources)
            setSpaceRoutes(parsedThings.routes)
            setSpacesLoading(false)
        } catch (error) {
            console.error('[spaces] loading the spaces failed:', error)
            // Let the next attempt (a retry) parse again
            parseConfigIdRef.current = null
            setSpacesError(true)
            setSpacesLoading(false)
        }
    }

    // Try again after a failure (the splash offers it)
    const reloadSpaces = async () => {
        setSpacesError(false)
        await parseRessources()
    }

    const switchSpace = async (spaceId: string | undefined, assistantId?: string) => {
        if (!spaceId) return turnSpaceOff()
        if (switchingRef.current) return
        switchingRef.current = true
        beginActivation()
        setActivationError(false)
        setSwitchingSpace(true)
        setSwitchingSpaceId(spaceId)

        // Resolve spaces state upfront so setSpaces can always be called in finally.
        const updatedSpaces = [...spaces]
        const newSpace = updatedSpaces.find(space => space.id === spaceId) ?? updatedSpaces[0]

        try {
            const assistantToActivateId = assistantId || newSpace?.defaultAssistant
            const activeAssistant = newSpace?.assistants.find(assistant => assistant.config.id === assistantToActivateId)

            // Keep the active flags in sync with the selected plant. Assistants in old/inactive spaces must not
            // remain marked active, or browser-back navigation can mistake them for the current assistant.
            updatedSpaces.forEach(space => {
                if (space === newSpace) {
                    space.isActive = true
                    space.assistants.forEach(assistant => {
                        if (assistant !== activeAssistant && assistant.isActive) assistant.deactivate()
                    })
                } else {
                    delete space.isActive
                    space.assistants.forEach(assistant => {
                        if (assistant.isActive) assistant.deactivate()
                    })
                }
            })

            const allowHistoryLoading = activeAssistant?.config?.aiConfig?.hasHistory

            const params = {
                space: newSpace?.endpoint || newSpace?.id,
                additionalBackendSupportedLanguages: newSpace?.additionalBackendSupportedLanguages,
                assistant: activeAssistant?.config.endpoint || activeAssistant?.config.oldId || activeAssistant?.config.id,
                assistantEndpoint: activeAssistant?.config.endpoint,
                userId: information.userId,
                token: await getApiToken(),
                path: `${newSpace?.path}/${activeAssistant?.path}`,
                spaceId: newSpace?.id,
                assistantId: activeAssistant?.config.id,
                icon: activeAssistant?.config.appearance.icon,
                iconSrc: activeAssistant?.config.appearance.iconSrc,
                object: 'chatFilter'
            }

            if (activeAssistant && allowHistoryLoading && !activeAssistant.config.historyLoaded) {
                const history = await loadAssistantHistory(activeAssistant, params)

                activeAssistant.updateHistory(history?.elements)
                activeAssistant.config.historyLoaded = true
            }

            try {
                await activeAssistant?.activate(params)
            } catch (activateError) {
                console.warn('[switchSpace] assistant activate failed:', activateError)
                setActivationError(true)
            }
            setSwitchingSpace(false)
        } catch (prepError) {
            console.warn('[switchSpace] failed to prepare or activate assistant:', prepError)
            setActivationError(true)
        } finally {
            // Always commit space state — even if getApiToken() or other prep fails —
            // so the home page is never permanently stuck in the loading state.
            setSpaces(updatedSpaces)
            setSwitchingSpace(false)
            switchingRef.current = false
            endActivation()
        }
    }

    // Try again after a failure (the splash offers it): the active space, or the one to start with
    const retryActivation = async () => {
        await switchSpace(getActiveSpace()?.id ?? config?.defaultSpace ?? spaces[0]?.id)
    }

    const loadSpaceHistory = async (spaceId: string): Promise<ChatMessage[]> => {
        const histories: ChatMessage[] = [];
        const space = spaces.find(space => space.id === spaceId);
        if (!space) return histories;

        const processAssistant = async (assistant: Assistant) => {
            if (!assistant.config.aiConfig?.hasHistory) return;

            if (!assistant.config.historyLoaded) {
                const params = {
                    space: space.endpoint || space.id,
                    assistant: assistant.config.endpoint || assistant.config.id,
                    userId: information.userId,
                    token: await getApiToken(),
                    path: `${space?.path}/${assistant?.path}`,
                    spaceId: space?.id,
                    assistantId: assistant?.config.id,
                    icon: assistant?.config.appearance.icon,
                    iconSrc: assistant?.config.appearance.iconSrc,
                };

                const history = await loadAssistantHistory(assistant, params);
                if (history?.elements) {
                    histories.push(...history.elements);
                    assistant.updateHistory(history.elements);
                }
            } else {
                histories.push(...(assistant.chatList ?? []).filter((chat): chat is ChatMessage => chat.chatId !== undefined));
            }

            assistant.config.historyLoaded = true;
        };

        for (const assistant of space.assistants) {
            await processAssistant(assistant);
        }

        histories.sort((a, b) => {
            const dateA = new Date(Number(a.timestamp)).getTime();
            const dateB = new Date(Number(b.timestamp)).getTime();
            return dateB - dateA;
        });

        return histories;
    };

    // switch assistant. The same switch asked twice while it runs (the click and the route that follows) is one switch
    const switchesRef = useRef(new Map<string, Promise<void>>())
    const switchAssistant = (assistantId: string, chatId?: string): Promise<void> => {
        const key = `${assistantId}:${chatId ?? ''}`
        const running = switchesRef.current.get(key)
        if (running) return running

        const switching = runSwitchAssistant(assistantId, chatId).finally(() => switchesRef.current.delete(key))
        switchesRef.current.set(key, switching)
        return switching
    }

    const runSwitchAssistant = async (assistantId: string, chatId?: string) => {
        const updatedSpaces = [...spaces]
        const targetSpace = updatedSpaces.find((space) =>
            space.assistants.some((assistant) => assistant.config.id === assistantId)
        )

        if (!targetSpace) {
            console.warn('No active space found for assistant:', assistantId)
            return
        }

        beginActivation()
        try {
            const newActiveAssistant = targetSpace.assistants.find(assistant => assistant.config.id === assistantId)
            const startsNewChat = !chatId && Boolean(newActiveAssistant?.activeChat)

            // Normalize every space, not just the target's flags. An assistant can remain active after its space
            // was switched away from, which makes route restoration mistake it for the selected assistant.
            updatedSpaces.forEach(space => {
                if (space === targetSpace) {
                    space.isActive = true
                    space.assistants.forEach(assistant => {
                        if (assistant !== newActiveAssistant && assistant.isActive) assistant.deactivate()
                    })
                } else {
                    delete space.isActive
                    space.assistants.forEach(assistant => {
                        if (assistant.isActive) assistant.deactivate()
                    })
                }
            })

            const allowHistoryLoading = newActiveAssistant?.config?.aiConfig?.hasHistory

            const params = {
                space: targetSpace?.endpoint || targetSpace?.id,
                additionalBackendSupportedLanguages: targetSpace?.additionalBackendSupportedLanguages,
                assistant: newActiveAssistant?.config.endpoint || newActiveAssistant?.config.oldId || newActiveAssistant?.config.id,
                assistantEndpoint: newActiveAssistant?.config.endpoint,
                userId: information.userId,
                token: await getApiToken(),
                path: `${targetSpace?.path}/${newActiveAssistant?.path}`,
                spaceId: targetSpace?.id,
                assistantId: newActiveAssistant?.config.id,
                icon: newActiveAssistant?.config.appearance.icon,
                iconSrc: newActiveAssistant?.config.appearance.iconSrc,
                object: 'chatFilter',
                chatId
            }

            if (newActiveAssistant && allowHistoryLoading && !newActiveAssistant.config.historyLoaded) {
                const history = await loadAssistantHistory(newActiveAssistant, params)

                newActiveAssistant.updateHistory(history?.elements)
                newActiveAssistant.config.historyLoaded = true
            }

            setActivationError(false)

            try {
                await newActiveAssistant?.activate(params)
                // activate() intentionally preserves an existing chat when no chatId is supplied (for example on a
                // language refresh). Explicit assistant selection is different: if this assistant already had a
                // chat open, start a fresh one instead of leaving the old conversation on screen.
                if (startsNewChat) await newActiveAssistant?.switchChat()
            } catch (activateError) {
                console.warn('[switchAssistant] assistant activate failed:', activateError)
                setActivationError(true)
            }

            setSpaces(updatedSpaces)
        } finally {
            endActivation()
        }
    }

    const turnSpaceOff = () => {
        const updatedSpaces = [...spaces]
        const newSpace = updatedSpaces.find(space => space.isActive)
        const activeAssistant = newSpace?.assistants?.find(assistant => assistant.config.id === newSpace.defaultAssistant)

        if (newSpace)
            delete newSpace.isActive

        activeAssistant?.deactivate()

        setSpaces(updatedSpaces)
    }

    const loadAssistantMetadata = useCallback(async (assistantId: string): Promise<void> => {
        const updatedSpaces = [...spaces]
        const space = updatedSpaces.find(s => s.assistants.some(a => a.config.id === assistantId))
        const assistant = space?.assistants.find(a => a.config.id === assistantId)
        if (!space || !assistant || assistant.metadata) return

        try {
            const token = await getApiToken()
            if ('getMetadata' in assistant.aiAdapter && typeof assistant.aiAdapter.getMetadata === 'function') {
                const result = await assistant.aiAdapter.getMetadata({
                    space: space.endpoint || space.id,
                    assistant: assistant.config.endpoint || assistant.config.oldId || assistant.config.id,
                    assistantEndpoint: assistant.config.endpoint,
                    userId: information.userId,
                    spaceId: space.id,
                    assistantId: assistant.config.id,
                    object: 'chatFilter',
                    token,
                })
                assistant.metadata = result?.metadata
                // If onboarding was the first thing loaded, leave a space active so the home page has a target.
                // Never mark a second space active just because its metadata was fetched.
                if (!updatedSpaces.some(candidate => candidate.isActive))
                    space.isActive = true
                setSpaces(updatedSpaces)
            }
        } catch (error) {
            console.warn('[loadAssistantMetadata] failed:', error)
        }
    }, [spaces, getApiToken, information.userId])

    const loadAssistantHistory = async (assistant: Assistant, params: ApiParameters) => {
        let history = undefined

        if (!assistant || !params) return history

        try {
            if ("getHistory" in assistant.aiAdapter && typeof assistant.aiAdapter.getHistory === "function")
                history = await assistant.aiAdapter.getHistory(0, params)
        } catch (error: unknown) {
            if (error instanceof Error && error.message?.includes('401')) {
                const userInformation = await rehydrateInformation()
                if (userInformation) {
                    params.userId = userInformation.userId
                    params.token = userInformation.idToken
                    loadAssistantHistory(assistant, params)
                }
            } else {
                console.warn('[loadAssistantHistory] history fetch failed, starting empty:', error)
            }
        }

        if (history?.elements) {
            const { path, spaceId, assistantId, icon, iconSrc } = params;
            const historyElements = history.elements.map((history: ChatMessage) => (
                { ...history, path, spaceId, assistantId, icon, iconSrc }
            ));
            history = { ...history, elements: historyElements }
        }

        return history
    }

    return (
        <SpaceContext.Provider value={{ spaces, spacesLoading, spaceRoutes, switchingSpace, switchingSpaceId, switchSpace, switchAssistant, loadAssistantMetadata, getActiveSpace, getActiveAssistant, loadSpaceHistory, getAssistantByPath, spacesError, reloadSpaces, activationError, retryActivation, isActivating }}>
            {children}
        </SpaceContext.Provider>
    )
}

export { SpaceProvider, SpaceContext }
